import { TZDate } from "@date-fns/tz";
import { endOfDay, startOfDay } from "date-fns";

import type { BidRequestFormValues, BidRequestPayload } from "./types";

/**
 * The UTC instant for the exact wall-clock date and time the user picked,
 * interpreted in the school's country timezone rather than the browser's, so a
 * delivery of "11 Oct, 14:30" lands at 14:30 *there*. Falls back to the browser
 * timezone when the country timezone is unknown.
 */
const zonedInstant = (date: Date, timeZone?: string): string => {
  if (!timeZone) {
    return date.toISOString();
  }

  const zoned = new TZDate(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    0,
    0,
    timeZone
  );

  // Wrap the epoch in a plain Date so toISOString() emits a UTC ("Z") string.
  return new Date(zoned.getTime()).toISOString();
};

/**
 * The UTC instant for the start or end of the calendar date the user picked (a
 * browser-local `Date` from the date picker), interpreted in the school's
 * country timezone rather than the browser's. The backend stores
 * `posting_end_date` as an absolute instant but reads it as a local calendar
 * date in the country timezone, so the instant we send must fall on the picked
 * date *there*. Falls back to the browser timezone when the country timezone is
 * unknown.
 */
const boundaryOfDay = (date: Date, edge: "start" | "end", timeZone?: string): string => {
  if (!timeZone) {
    return (edge === "end" ? endOfDay(date) : startOfDay(date)).toISOString();
  }

  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const zoned =
    edge === "end"
      ? new TZDate(year, month, day, 23, 59, 59, 999, timeZone)
      : new TZDate(year, month, day, 0, 0, 0, 0, timeZone);

  // Wrap the epoch in a plain Date so toISOString() emits a UTC ("Z") string.
  return new Date(zoned.getTime()).toISOString();
};

export const toBidRequestPayload = (
  values: BidRequestFormValues,
  timeZone?: string
): BidRequestPayload => {
  const deliveryLocation = values.delivery_location?.trim();

  return {
    delivery_date: zonedInstant(values.delivery_date as Date, timeZone),
    ...(deliveryLocation ? { delivery_location: deliveryLocation } : {}),
    posting_start_date: boundaryOfDay(values.posting_start_date as Date, "start", timeZone),
    posting_end_date: boundaryOfDay(values.posting_end_date as Date, "end", timeZone),
    items: values.items.map(item => ({
      commodity: Number(item.commodity),
      quantity: Number(item.quantity),
      unit_of_measure: Number(item.unit_of_measure),
      quality: item.quality,
    })),
  };
};
